/**
 * `ProtocolStrip` — founder 2026-10-29. One compact block of protocol
 * chips on the completion screen, replacing the heading, paragraph and
 * tile grid of `ProtocolStackSection`. Built from PROTOCOL_REGISTRY (the
 * ONE source, ADR-0213) so a new protocol, such as PAP, can never drop
 * off this screen again.
 *
 * Round 2: a stronger line ("Ready for {n} agent protocols"), the
 * protocols that are not live sit after a muted "Coming" label, and every
 * chip carries a tooltip (long name, one plain line, status). A chip
 * opens its docs page. No "All protocols" link: this is the full list.
 */

import { PROTOCOL_REGISTRY, type ProtocolEntry } from '@sill/types';

import { Tooltip } from '../../components/atoms/Tooltip.js';
import { useC } from '../../lib/theme.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";

export const DOCS_ORIGIN = 'https://docs.sill.so';

/** Live protocols in registry order. */
export function liveProtocols(): readonly ProtocolEntry[] {
  return PROTOCOL_REGISTRY.filter((p) => p.status === 'live');
}

/** Not live yet: built-not-enabled first, then the rest. */
export function comingProtocols(): readonly ProtocolEntry[] {
  const rank = (p: ProtocolEntry): number => (p.status === 'built_not_enabled' ? 0 : 1);
  return PROTOCOL_REGISTRY.filter((p) => p.status !== 'live').sort((a, b) => rank(a) - rank(b));
}

/** Plain status word for the tooltip. */
export function chipStatus(p: ProtocolEntry): string {
  switch (p.status) {
    case 'live': return p.bound !== null ? `Live · ${p.bound}` : 'Live';
    case 'built_not_enabled': return 'Built, not enabled';
    case 'deferred': return 'Deferred';
    case 'roadmap': return 'On the roadmap';
    case 'not_started': return 'Not started';
  }
}

function Chip({ p }: { p: ProtocolEntry }): JSX.Element {
  const C = useC();
  const live = p.status === 'live';
  const dot = live ? C.mintFg : p.status === 'built_not_enabled' ? C.skyFg : C.dim;
  return (
    <Tooltip
      side="top"
      data-testid={`protocol-tip-${p.id}`}
      content={
        <span style={{ display: 'block' }}>
          <span style={{ display: 'block', fontWeight: 600 }}>{p.long_name}</span>
          <span style={{ display: 'block', marginTop: 2 }}>{p.summary}</span>
          <span style={{ display: 'block', marginTop: 4, fontFamily: monoFont, fontSize: 10, letterSpacing: 0.6, opacity: 0.8 }}>
            {chipStatus(p).toUpperCase()}
          </span>
        </span>
      }
    >
      <a
        href={`${DOCS_ORIGIN}${p.docs_path}`}
        target="_blank"
        rel="noreferrer"
        aria-label={`${p.long_name}: ${chipStatus(p)}. Opens the docs.`}
        data-testid={`protocol-chip-${p.id}`}
        data-status={p.status}
        className="inline-flex items-center gap-1.5"
        style={{
          fontFamily: monoFont, fontSize: 10.5, letterSpacing: 0.4, whiteSpace: 'nowrap',
          color: live ? C.text : C.muted, textDecoration: 'none',
          border: `1px solid ${C.border}`, borderRadius: 2, padding: '3px 8px',
        }}
      >
        <span aria-hidden style={{ width: 6, height: 6, borderRadius: 3, background: dot, flexShrink: 0 }} />
        {p.name}
      </a>
    </Tooltip>
  );
}

export function ProtocolStrip(): JSX.Element {
  const C = useC();
  const live = liveProtocols();
  const coming = comingProtocols();
  return (
    <div data-testid="protocol-strip">
      <div data-testid="protocol-strip-heading" style={{ fontFamily: sansFont, fontSize: 15, fontWeight: 500, color: C.text, margin: '0 0 10px' }}>
        Ready for {live.length} agent protocols
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        {live.map((p) => <Chip key={p.id} p={p} />)}
      </div>
      {/* Its own line at every width, so "Coming" never lands mid-row. */}
      {coming.length > 0 ? (
        <div className="flex flex-wrap items-center gap-1.5 mt-2">
          <span
            data-testid="protocol-strip-coming"
            style={{ fontFamily: monoFont, fontSize: 9.5, letterSpacing: 1.2, color: C.dim, marginRight: 4 }}
          >
            COMING
          </span>
          {coming.map((p) => <Chip key={p.id} p={p} />)}
        </div>
      ) : null}
    </div>
  );
}
